/**
 * Localization Context
 * Provides translation and formatting functions to shared-ui components
 * Apps (qs2, qs-status-frontend) provide the actual implementation
 */

import { createContext, useContext } from 'react';

export type LocaleCode = string; // e.g., 'en-US', 'fi-FI', 'de-DE'

export interface DateFormatOptions {
  format?: string;
  includeTime?: boolean;
  timeZone?: string;
}

export interface LocalizationContextValue {
  /**
   * Current locale code (e.g., 'en-US')
   */
  locale: string;

  /**
   * Translate a key with optional interpolation values
   */
  t: (key: string, options?: Record<string, unknown>) => string;

  /**
   * Format a date according to locale settings
   */
  formatDate: (
    date: Date | string | number,
    options?: DateFormatOptions
  ) => string;

  /**
   * Format a time according to locale settings
   */
  formatTime: (date: Date | string | number) => string;

  /**
   * Format a date and time according to locale settings
   */
  formatDateTime: (date: Date | string | number) => string;

  /**
   * Format a number according to locale settings
   */
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;

  /**
   * Get the date format pattern (e.g., 'MM/DD/YYYY' or 'DD.MM.YYYY')
   */
  dateFormat: string;

  /**
   * Get the time format pattern (e.g., '12' or '24')
   */
  timeFormat: '12' | '24';
}

const defaultContext: LocalizationContextValue = {
  locale: 'en-US',
  t: (key: string) => key,
  formatDate: (date: Date | string | number) =>
    new Date(date).toLocaleDateString(),
  formatTime: (date: Date | string | number) =>
    new Date(date).toLocaleTimeString(),
  formatDateTime: (date: Date | string | number) =>
    new Date(date).toLocaleString(),
  formatNumber: (value: number) => value.toString(),
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12',
};

export const LocalizationContext =
  createContext<LocalizationContextValue>(defaultContext);

/**
 * Hook to access localization functions in shared-ui components
 */
export function useLocalization(): LocalizationContextValue {
  const context = useContext(LocalizationContext);

  if (!context) {
    console.warn(
      'useLocalization: No LocalizationProvider found in tree, using defaults'
    );
    return defaultContext;
  }

  return context;
}

/**
 * Props for LocalizationProvider
 */
export interface LocalizationProviderProps {
  children: React.ReactNode;
  value: LocalizationContextValue;
}

/**
 * Provider component that apps use to provide localization to shared-ui
 *
 * @example
 * ```tsx
 * import { LocalizationProvider } from '@emfit/shared-ui/contexts/LocalizationContext';
 * import { t } from './i18n';
 *
 * <LocalizationProvider value={{
 *   locale: 'en-US',
 *   t: (key, options) => t(key, options),
 *   formatDate: (date) => format(new Date(date), dateFormat),
 *   ...
 * }}>
 *   <App />
 * </LocalizationProvider>
 * ```
 */
export function LocalizationProvider({
  children,
  value,
}: LocalizationProviderProps) {
  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}
